import { DecimalPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { BadgePipe } from 'app/core/pipes/badge.pipe';
import { StatisticsService } from 'app/modules/statistics/statistics.service';
import { AuthService } from '../../../auth/auth.service';
import { EserviceService } from '../../../eservices/eservice.service';

type EserviceRow = {
  name: string;
  count: number;
  icon: string;
  color: string;
  hex: string;
  queryParams: any;
};

@Component({
  selector: 'app-my-statistics-counts',
  standalone: true,
  imports: [RouterLink, DecimalPipe, NgClass, MatIconModule, MatTooltipModule, BadgePipe],
  templateUrl: './my-statistics-counts.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyStatisticsCountsComponent implements OnChanges {
  private Statistics = inject(StatisticsService);
  private auth = inject(AuthService);
  private eservicesService = inject(EserviceService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  profile: any;
  loading: number = 0;
  error: string | null = null;

  // Aggregated card data
  eservices: EserviceRow[] = [];
  happinessCenterOnline = 0;
  callCenter: { Hold: number; InProcess: number; Open: number } = { Hold: 0, InProcess: 0, Open: 0 };
  tasksData: { Total: number; Open: number; Resolved: number } = { Total: 0, Open: 0, Resolved: 0 };
  incomingPaymentsData: { Total: number; Open: number; Resolved: number } = { Total: 0, Open: 0, Resolved: 0 };
  vendorBills = 0;
  workOrders = 0;
  rfps = 0;

  eserviceKeys: any = {
    AD: { color: 'text-cyan-600', hex: '#0891b2', icon: 'contact_page', route: '/eservices/list' },
    MI: { color: 'text-red-600', hex: '#dc2626', icon: 'local_shipping', route: '/eservices/list' },
    MO: { color: 'text-yellow-600', hex: '#ca8a04', icon: 'local_shipping', route: '/eservices/list' },
    WP: { color: 'text-lime-600', hex: '#65a30d', icon: 'format_paint', route: '/eservices/list' },
    FO: { color: 'text-purple-600', hex: '#9333ea', icon: 'build', route: '/eservices/list' },
    HB: { color: 'text-green-600', hex: '#16a34a', icon: 'movie', route: '/eservices/list' },
    TP: { color: 'text-teal-600', hex: '#0d9488', icon: 'handshake', route: '/eservices/list' },
    CS: { color: 'text-pink-500', hex: '#ec4899', icon: 'shopping_cart', route: '/eservices/list' },
    RI: { color: 'text-slate-500', hex: '#64748b', icon: 'edit', route: '/eservices/list' },
    DP: { color: 'text-blue-600', hex: '#2563eb', icon: 'description', route: '/eservices/list' },
    SS: { color: 'text-indigo-600', hex: '#4f46e5', icon: 'home', route: '/eservices/list' },
    CCS: { color: 'text-green-700', hex: '#15803d', icon: 'handyman', route: '/eservices/list' },
    DV: { color: 'text-blue-600', hex: '#2563eb', icon: 'menu_book', route: '/eservices/owner-kyc' },
  };

  @Input() fromDate: any = '';
  @Input() toDate: any = '';
  @Input() associationId: string | null = null;

  get dataLoader(): boolean {
    return this.loading > 0;
  }

  ngOnChanges(changes: SimpleChanges) {
    this.profile = this.auth.profile();
    this.getData();
  }

  private getData() {
    const filter: any = { fromDate: this.fromDate, toDate: this.toDate, my: 1 };
    if (this.associationId) filter.association_id = this.associationId;

    // Reset
    this.eservices = [];
    this.happinessCenterOnline = 0;
    this.callCenter = { Hold: 0, InProcess: 0, Open: 0 };
    this.tasksData = { Total: 0, Open: 0, Resolved: 0 };
    this.incomingPaymentsData = { Total: 0, Open: 0, Resolved: 0 };
    this.vendorBills = 0;
    this.workOrders = 0;
    this.rfps = 0;

    // 1) E-services pending counts -> top 4
    this.loading++;
    this.Statistics.getServicesCountByStatus(filter)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const eserviceTypes = response.record.StatusWise || {};
          const eservicesList = [...this.eservicesService.eServicesList(), { name: 'Owner KYC', key: 'DV' }];
          const all: EserviceRow[] = [];
          Object.keys(eserviceTypes).forEach((key: string) => {
            if (eserviceTypes[key].Pending) {
              all.push({
                name: eservicesList.find((service) => service.key == key)?.name || key,
                count: eserviceTypes[key].Pending,
                color: this.eserviceKeys[key] ? this.eserviceKeys[key].color : '',
                hex: this.eserviceKeys[key] ? this.eserviceKeys[key].hex : '#64748b',
                icon: this.eserviceKeys[key] ? this.eserviceKeys[key].icon : 'handyman',
                queryParams: { _ref: 'dashboard', type: key },
              });
            }
          });
          this.eservices = all.sort((a, b) => b.count - a.count).slice(0, 4);
          this.loading--;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.error = error.message || 'An error occurred while getting the Statistic data';
          this.loading--;
          this.cdr.markForCheck();
        },
      });

    // 2a) Happiness Center - Online (enquiries)
    this.loading++;
    this.Statistics.getEnquiriesCountByStatus(filter)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.happinessCenterOnline = response.record.Open || 0;
          this.loading--;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.error = error.message || 'An error occurred while getting the Statistic data';
          this.loading--;
          this.cdr.markForCheck();
        },
      });

    // 2b) Happiness Center - Call Center counts
    this.loading++;
    this.Statistics.getCallCenterTicketCounts(filter)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const rec = response.record || {};
          this.callCenter = {
            Hold: rec.Hold || 0,
            InProcess: rec.InProcess || 0,
            Open: rec.Open || 0,
          };
          this.loading--;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.error = error.message || 'An error occurred while getting the Statistic data';
          this.loading--;
          this.cdr.markForCheck();
        },
      });

    // 3) Tasks
    this.loading++;
    this.Statistics.getTasksCountByStatus(filter)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const r = response.record ?? {};
          this.tasksData = { Total: r.Total || 0, Open: r.Open || 0, Resolved: r.Resolved || 0 };
          this.loading--;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.error = error.message || 'An error occurred while getting the Statistic data';
          this.loading--;
          this.cdr.markForCheck();
        },
      });

    // 4a) Incoming Payments
    this.loading++;
    this.Statistics.getIncomingPaymentsCountByStatus(filter)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const r = response.record ?? {};
          this.incomingPaymentsData = { Total: r.Total || 0, Open: r.Open || 0, Resolved: r.Resolved || 0 };
          this.loading--;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.error = error.message || 'An error occurred while getting the Statistic data';
          this.loading--;
          this.cdr.markForCheck();
        },
      });

    // 4b) Vendor Bills
    this.loading++;
    this.Statistics.getBillsCountByStatus(filter)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.vendorBills = response.record.Total || 0;
          this.loading--;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.error = error.message || 'An error occurred while getting the Statistic data';
          this.loading--;
          this.cdr.markForCheck();
        },
      });

    // 5a) Work Orders
    this.loading++;
    this.Statistics.getWorkOrdersCountByStatus(filter)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.workOrders = response.record.count || 0;
          this.loading--;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.error = error.message || 'An error occurred while getting the Statistic data';
          this.loading--;
          this.cdr.markForCheck();
        },
      });

    // 5b) RFPs
    this.loading++;
    this.Statistics.getRfpsCountByStatus(filter)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.rfps = response.record.count || 0;
          this.loading--;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.error = error.message || 'An error occurred while getting the Statistic data';
          this.loading--;
          this.cdr.markForCheck();
        },
      });
  }
}
